import React, { useState, useEffect, useCallback } from 'react';
import { X, Lock, Loader2, Pencil, Trash2, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { groupsApi } from '../../services/api';
import type { Group, GroupRule, Pagination } from '../../types';
import AddRuleToGroupModal from './AddRuleToGroupModal';
import EditRuleArgsModal from './EditRuleArgsModal';

const TYPE_LABELS: Record<string, string> = {
  V: 'Validation',
  R: 'Reconciliation',
};

const TYPE_COLORS: Record<string, string> = {
  V: 'bg-green-500/20 text-green-400 border-green-500/30',
  R: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
};

interface Props {
  group: Group;
  onClose: () => void;
  onGroupChanged?: () => void;
}

const GroupDetailModal: React.FC<Props> = ({ group, onClose, onGroupChanged }) => {
  const [rules, setRules] = useState<GroupRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const perPage = 10;

  // Enriched group info from the rules API (has project.id etc.)
  const [groupInfo, setGroupInfo] = useState<{ projectId: number; projectName: string; groupName: string }>({
    projectId: group.project?.id || 0,
    projectName: group.project?.name || '',
    groupName: group.name,
  });

  const [addRuleOpen, setAddRuleOpen] = useState(false);
  const [editRule, setEditRule] = useState<GroupRule | null>(null);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await groupsApi.getRules(group.id, { page, items_per_page: perPage });
      const payload = resp.data.payload;
      const grp = payload?.group;
      const list = grp?.applied_rules || [];
      setRules(Array.isArray(list) ? list : []);
      // Store enriched project/group info from API
      if (grp?.project) {
        setGroupInfo({ projectId: grp.project.id, projectName: grp.project.name, groupName: grp.name });
      }
      const pag = payload?.pagination?.pagination;
      if (pag) {
        setTotalPages(pag.pages || Math.ceil((pag.count || 0) / perPage) || 1);
      } else {
        setTotalPages(1);
      }
    } catch {
      setRules([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [group.id, page]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const handleDeleteRule = async (r: GroupRule) => {
    if (!window.confirm(`Delete rule "${r.rule}" from this group?`)) return;
    try {
      await groupsApi.deleteRule(r.id, { project_id: groupInfo.projectId, group_id: group.id });
      fetchRules();
      onGroupChanged?.();
    } catch {
      // silent
    }
  };

  const handleRulesAdded = () => {
    setAddRuleOpen(false);
    fetchRules();
    onGroupChanged?.();
  };

  const handleRuleSaved = () => {
    setEditRule(null);
    fetchRules();
    onGroupChanged?.();
  };

  const typeCls = TYPE_COLORS[group.type] || 'bg-cargo-panel text-cargo-muted border-cargo-border';

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
        <div
          className="bg-cargo-surface border border-cargo-border rounded-xl w-full max-w-4xl max-h-[85vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between px-6 py-4 border-b border-cargo-border">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-cargo-text">{group.name}</h2>
                {group.meta?.locked && (
                  <span title="Locked"><Lock className="w-4 h-4 text-yellow-500 flex-shrink-0" /></span>
                )}
                <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border ${typeCls}`}>
                  {TYPE_LABELS[group.type] || group.type}
                </span>
              </div>
              {group.description && (
                <p className="text-xs text-cargo-muted mt-1">{group.description}</p>
              )}
            </div>
            <button onClick={onClose} className="ml-4 text-cargo-muted hover:text-cargo-text flex-shrink-0">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body — Rules table */}
          <div className="flex-1 overflow-y-auto">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-cargo-border">
                    <th className="px-4 py-3 text-left text-xs font-medium text-cargo-muted uppercase tracking-wider">Rule Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-cargo-muted uppercase tracking-wider">Column</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-cargo-muted uppercase tracking-wider">Severity</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-cargo-muted uppercase tracking-wider">Package</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-cargo-muted uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="py-16 text-center">
                        <Loader2 className="w-5 h-5 animate-spin text-cargo-accent mx-auto" />
                        <p className="text-cargo-muted text-sm mt-2">Loading rules...</p>
                      </td>
                    </tr>
                  ) : rules.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-16 text-center text-cargo-muted text-sm">
                        No rules in this group.
                      </td>
                    </tr>
                  ) : (
                    rules.map((r) => (
                      <tr key={r.id} className="border-b border-cargo-border/50 hover:bg-cargo-panel/50 transition-colors">
                        <td className="px-4 py-3 max-w-[240px]">
                          <p className="text-cargo-text font-mono text-sm truncate" title={r.rule}>{r.rule}</p>
                          {r.description && (
                            <p className="text-[11px] text-cargo-muted truncate mt-0.5">{r.description}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-cargo-muted whitespace-nowrap">
                          {r.args?.column || '—'}
                        </td>
                        <td className="px-4 py-3 text-cargo-muted whitespace-nowrap">
                          {r.args?.severity || r.severity || '—'}
                        </td>
                        <td className="px-4 py-3 text-cargo-muted whitespace-nowrap text-xs">
                          {r.package || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => setEditRule(r)}
                              className="p-1.5 rounded-lg text-cargo-muted hover:text-cargo-accent hover:bg-cargo-accent/10 transition-colors"
                              title="Edit args"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDeleteRule(r)}
                              className="p-1.5 rounded-lg text-cargo-muted hover:text-red-400 hover:bg-red-400/10 transition-colors"
                              title="Delete rule"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!loading && totalPages > 1 && (
              <div className="flex items-center justify-end gap-3 px-4 py-3 border-t border-cargo-border">
                <span className="text-sm text-cargo-muted">Page {page} of {totalPages}</span>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="p-1.5 rounded-lg text-cargo-muted hover:text-cargo-text hover:bg-cargo-panel
                               disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="p-1.5 rounded-lg text-cargo-muted hover:text-cargo-text hover:bg-cargo-panel
                               disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-3 border-t border-cargo-border">
            <div className="flex items-center gap-4 text-xs text-cargo-muted">
              <span>Owner: <span className="text-cargo-text">{group.owner}</span></span>
              {group.project && (
                <span>Project: <span className="text-cargo-text">{group.project.name}</span></span>
              )}
              {group.tags?.length > 0 && (
                <div className="flex items-center gap-1">
                  {group.tags.map((tag) => (
                    <span key={tag} className="px-1.5 py-0.5 text-[10px] rounded bg-purple-500/10 text-purple-400 border border-purple-500/20">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => setAddRuleOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-cargo-accent text-white
                         hover:bg-cargo-accent/90 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Rules
            </button>
          </div>
        </div>
      </div>

      {/* Sub-modals */}
      {addRuleOpen && (
        <AddRuleToGroupModal
          groupId={group.id}
          groupName={groupInfo.groupName}
          projectName={groupInfo.projectName}
          onClose={() => setAddRuleOpen(false)}
          onAdded={handleRulesAdded}
        />
      )}

      {editRule && (
        <EditRuleArgsModal
          rule={editRule}
          groupId={group.id}
          groupName={groupInfo.groupName}
          projectName={groupInfo.projectName}
          onClose={() => setEditRule(null)}
          onSaved={handleRuleSaved}
        />
      )}
    </>
  );
};

export default GroupDetailModal;
